import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import BrandCombobox from '@/components/BrandCombobox';
import { 
  ArrowLeft, Car, CheckCircle2, AlertTriangle, ArrowRightLeft,
  Loader2, CreditCard, Sparkles, Trash2, Search
} from 'lucide-react';

const VEHICLE_TYPE_DISPLAY = {
  small: 'Carro Pequeno',
  suv: 'SUV',
  pickup: 'Caminhonete'
};

export default function MigratePlan() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [currentVehicle, setCurrentVehicle] = useState(null);
  const [plans, setPlans] = useState([]);
  
  // Etapa do fluxo: 'select' | 'vehicle_form' | 'plan_select' | 'preview'
  const [step, setStep] = useState('select');
  
  // Seleções do usuário
  const [changeType, setChangeType] = useState(null); // 'vehicle' | 'plan'
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Formulário de novo veículo
  const [brands, setBrands] = useState([]);
  const [newVehicle, setNewVehicle] = useState({ brand: '', model: '', plate: '', vehicle_type: '' });
  const [classifiedType, setClassifiedType] = useState(null);
  const [needsManualSelection, setNeedsManualSelection] = useState(false);
  
  // Autocomplete do modelo
  const [modelInput, setModelInput] = useState('');
  const [modelSuggestions, setModelSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingModels, setSearchingModels] = useState(false);
  const modelInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Preview e processamento
  const [preview, setPreview] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          modelInputRef.current && !modelInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce para busca de modelos
  useEffect(() => {
    const timer = setTimeout(() => {
      if (modelInput && newVehicle.brand) {
        searchModels(modelInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [modelInput, newVehicle.brand]);

  // Buscar modelos com autocomplete
  const searchModels = async (query) => {
    if (!newVehicle.brand || query.length < 1) {
      setModelSuggestions([]);
      return;
    }
    
    setSearchingModels(true);
    try {
      const res = await axios.get(`${API}/vehicles/v2/models`, {
        params: { brand: newVehicle.brand, q: query, limit: 10 }
      });
      setModelSuggestions(res.data.models || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Erro ao buscar modelos:', error);
      setModelSuggestions([]);
    } finally {
      setSearchingModels(false);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [subRes, brandsRes] = await Promise.all([
        axios.get(`${API}/client/subscription-details`, { headers }),
        axios.get(`${API}/vehicles/v2/brands?limit=100`).catch(() => axios.get(`${API}/vehicles/brands`))
      ]);
      
      if (!subRes.data.has_active_plan) {
        toast.error('Você não possui um plano ativo');
        navigate('/client/dashboard');
        return;
      }
      
      setSubscription(subRes.data.subscription);
      setCurrentVehicle(subRes.data.subscription?.vehicle);
      setBrands(brandsRes.data.brands || []);
      
      // Buscar planos disponíveis para o tipo de veículo atual
      const vehicleType = subRes.data.subscription?.vehicle?.vehicle_type || 'small';
      try {
        const plansRes = await axios.get(`${API}/vehicles/plans-by-type/${vehicleType}`);
        // Converter objeto de planos para array
        const plansData = plansRes.data.plans;
        if (plansData && typeof plansData === 'object' && !Array.isArray(plansData)) {
          // API retorna objeto {key: {...plan...}}, converter para array com key incluída
          const plansArray = Object.entries(plansData).map(([key, plan]) => ({
            ...plan,
            key: key
          }));
          setPlans(plansArray);
        } else if (Array.isArray(plansData)) {
          setPlans(plansData);
        } else {
          setPlans([]);
        }
      } catch (e) {
        setPlans([]);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleBrandChange = (brand) => {
    setNewVehicle({ ...newVehicle, brand, model: '', vehicle_type: '' });
    setModelInput('');
    setModelSuggestions([]);
    setClassifiedType(null);
    setNeedsManualSelection(false);
  };

  const handleModelInputChange = (e) => {
    const value = e.target.value;
    setModelInput(value);
    setNewVehicle(prev => ({ ...prev, model: '', vehicle_type: '' }));
    setClassifiedType(null);
    setNeedsManualSelection(false);
    
    if (value.length === 0) {
      setModelSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectModel = (modelData) => {
    setModelInput(modelData.modelo);
    setNewVehicle(prev => ({ 
      ...prev, 
      model: modelData.modelo, 
      vehicle_type: modelData.vehicle_type || '' 
    }));
    setShowSuggestions(false);
    
    if (modelData.vehicle_type) {
      setClassifiedType({
        vehicle_type: modelData.vehicle_type,
        vehicle_type_display: modelData.vehicle_type_display
      });
      setNeedsManualSelection(false);
    } else {
      setNeedsManualSelection(true);
    }
  };

  const handleSelectOption = (option) => {
    setChangeType(option);
    if (option === 'vehicle') {
      setStep('vehicle_form');
    } else if (option === 'plan') {
      setStep('plan_select');
    }
  };

  const handleVehiclePreview = async () => {
    if (!newVehicle.brand || !newVehicle.model || !newVehicle.plate) {
      toast.error('Preencha todos os campos do veículo');
      return;
    }
    if (needsManualSelection && !newVehicle.vehicle_type) {
      toast.error('Selecione o tipo do veículo');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // 1. Primeiro cadastrar o novo veículo temporariamente
      const vehicleRes = await axios.post(`${API}/client/vehicle`, newVehicle, { headers });
      const newVehicleId = vehicleRes.data.vehicle.id;
      
      // 2. Usar o endpoint de preview do backend para calcular diferença
      const previewRes = await axios.post(`${API}/client/vehicle/change-preview`, {
        new_vehicle_id: newVehicleId
      }, { headers });
      
      const data = previewRes.data;
      
      setPreview({
        type: 'vehicle',
        newVehicleId: newVehicleId, // Guardar ID para usar na confirmação
        currentVehicle: data.current_vehicle,
        newVehicle: data.new_vehicle,
        currentPlan: data.current_plan,
        newPlan: data.new_plan,
        adjustment: data.adjustment,
        servicesRemaining: data.services_remaining,
        willRemoveOldVehicle: true
      });
      
      setStep('preview');
    } catch (error) {
      console.error('Erro ao calcular preview:', error);
      toast.error(error.response?.data?.detail || 'Erro ao calcular preview');
    } finally {
      setProcessing(false);
    }
  };

  const handlePlanPreview = async () => {
    if (!selectedPlan) {
      toast.error('Selecione um plano');
      return;
    }

    setProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // FASE 11.2K(iii) — Assinatura Pagar.me: NÃO consultar o Stripe. A regra JATOO é
      // DIFERENÇA CHEIA (novo - atual, sem proração). Downgrade não cobra.
      if (subscription?.payment_provider === 'pagarme') {
        const currentPrice = subscription?.plan?.price || 0;
        const newPrice = selectedPlan.price || 0;
        const diff = Math.round((newPrice - currentPrice) * 100) / 100;
        const isUpgrade = diff > 0;
        const isDowngrade = diff < 0;
        const realAmount = isUpgrade ? diff : 0;
        setPreview({
          type: 'plan',
          gateway: 'pagarme',
          idempotencyKey: (window.crypto?.randomUUID?.() || `pg_${subscription?.id}_${Date.now()}`),
          currentPlan: subscription?.plan,
          newPlan: selectedPlan,
          adjustment: {
            type: isUpgrade ? 'upgrade' : (isDowngrade ? 'downgrade' : 'same'),
            priceDifference: realAmount,
            requires_payment: isUpgrade,
            realAmount: realAmount,
            isStripeReal: false,
            message: isUpgrade
              ? `Upgrade de plano. Diferença a pagar agora: R$ ${realAmount.toFixed(2)}`
              : isDowngrade
              ? `Você manterá os benefícios do plano atual até a próxima renovação. A partir dela, o valor mensal passará a ser do novo plano.`
              : 'Troca imediata sem custos adicionais.'
          }
        });
        setStep('preview');
        setProcessing(false);
        return;
      }

      // CRITICAL FIX: Consultar o Stripe para obter valor real de proration
      const previewRes = await axios.post(`${API}/client/plan/stripe-preview`, {
        new_plan_key: selectedPlan.id || selectedPlan.plan_key
      }, { headers });
      
      const stripePreview = previewRes.data;
      
      // Determinar tipo de ajuste
      let adjustmentType = 'same';
      if (stripePreview.is_upgrade) {
        adjustmentType = 'upgrade';
      } else if (stripePreview.is_downgrade) {
        adjustmentType = 'downgrade';
      }

      // Usar o valor REAL do Stripe (ou cálculo local se Stripe não disponível)
      const realAmount = stripePreview.amount_due || 0;
      const isStripeReal = stripePreview.stripe_preview_available;
      
      setPreview({
        type: 'plan',
        currentPlan: subscription?.plan,
        newPlan: selectedPlan,
        stripePreview: stripePreview, // Guardar dados completos do Stripe
        adjustment: {
          type: adjustmentType,
          priceDifference: realAmount,
          requires_payment: adjustmentType === 'upgrade' && realAmount > 0,
          realAmount: realAmount, // Valor REAL a ser cobrado
          isStripeReal: isStripeReal, // Indica se veio do Stripe
          message: adjustmentType === 'upgrade' 
            ? isStripeReal 
              ? `Upgrade de plano. Valor proporcional: R$ ${realAmount.toFixed(2)} (calculado pelo Stripe)`
              : `Upgrade de plano. Valor estimado: R$ ${realAmount.toFixed(2)} (valor final pode variar)`
            : adjustmentType === 'downgrade'
            ? `Você manterá os benefícios do plano atual até a próxima renovação. A partir dela, o valor mensal passará a ser do novo plano.`
            : 'Troca imediata sem custos adicionais.'
        }
      });
      
      setStep('preview');
    } catch (error) {
      console.error('Erro ao calcular preview:', error);
      toast.error(error.response?.data?.detail || 'Erro ao calcular preview');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirm = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (preview?.type === 'vehicle') {
        // Usar o ID do veículo já cadastrado no preview
        const vehicleId = preview.newVehicleId;

        // FASE 11.2L — Assinatura Pagar.me: troca de veículo INLINE (sem Stripe Checkout).
        // UPGRADE (com diferença) abre a tela de pagamento Pagar.me; downgrade/mesmo tipo
        // aplica direto (sem cobrança). Stripe legado permanece no fluxo abaixo.
        if (subscription?.payment_provider === 'pagarme') {
          const idemKey = window.crypto?.randomUUID?.() || `veh_${subscription?.id}_${Date.now()}`;
          if (preview.adjustment?.requires_payment && preview.adjustment?.amount > 0) {
            navigate('/client/payment', {
              state: {
                upgrade_pagarme: true,
                vehicle_change: true,
                new_vehicle_id: vehicleId,
                idempotency_key: idemKey,
                plan_name: preview.newPlan?.name,
                plan_price: preview.newPlan?.price,
                diff_amount: preview.adjustment.amount,
                current_vehicle_label: `${preview.currentVehicle?.brand || ''} ${preview.currentVehicle?.model || ''}`.trim(),
                new_vehicle_label: `${preview.newVehicle?.brand || ''} ${preview.newVehicle?.model || ''}`.trim(),
              },
            });
            return;
          }
          const response = await axios.post(`${API}/client/vehicle/change-pagarme`, {
            new_vehicle_id: vehicleId,
            idempotency_key: idemKey,
          }, { headers });
          toast.success(response.data.message || 'Veículo alterado com sucesso!');
          navigate('/client/dashboard');
          return;
        }

        // Em apps nativos (Capacitor) usamos a URL HTTPS de produção como origin_url.
        // O retorno do Stripe chega via Android App Link / iOS Universal Link
        // (Digital Asset Links verifica o domínio jatoo.app.br via /.well-known/).
        const isNative = Capacitor.isNativePlatform();
        const originUrl = isNative ? 'https://jatoo.app.br' : window.location.origin;

        // Efetuar troca de veículo no plano
        const response = await axios.post(`${API}/client/vehicle/change`, {
          new_vehicle_id: vehicleId,
          idempotency_key: `migrate_vehicle_${subscription?.id}_${Date.now()}`,
          origin_url: originUrl
        }, { headers });
        
        // Verificar se requer pagamento (upgrade)
        if (response.data.requires_payment && response.data.checkout_url) {
          // Redirecionar para Stripe Checkout
          toast.info('Redirecionando para pagamento...');
          if (isNative) {
            await Browser.open({ url: response.data.checkout_url });
          } else {
            window.location.href = response.data.checkout_url;
          }
          return; // Não continuar - usuário será redirecionado
        }
        
        // Troca aplicada imediatamente (mesmo tipo ou downgrade)
        toast.success(response.data.message || 'Veículo alterado com sucesso!');
        
        // No downgrade navega direto para dashboard
        if (response.data.change_type === 'downgrade') {
          navigate('/client/dashboard');
        } else {
          navigate('/client/plan-details');
        }
        
      } else if (preview?.type === 'plan') {
        // Troca de plano - usar novo endpoint
        // Usar id, plan_key ou key conforme disponível
        const planKey = preview.newPlan.id || preview.newPlan.plan_key || preview.newPlan.key;

        // FASE 11.2K-III.1 — Assinatura Pagar.me: fluxo INLINE dentro da JATOO (sem redirect
        // ao Stripe). UPGRADE abre a tela de pagamento Pagar.me para cobrar a diferença CHEIA
        // (tokenização client-side); a troca só é aplicada após a cobrança aprovada.
        // DOWNGRADE não cobra → aplica direto.
        if (preview.gateway === 'pagarme' || subscription?.payment_provider === 'pagarme') {
          if (preview.adjustment?.requires_payment && preview.adjustment?.realAmount > 0) {
            navigate('/client/payment', {
              state: {
                upgrade_pagarme: true,
                target_plan_id: planKey,
                idempotency_key: preview.idempotencyKey,
                plan_name: preview.newPlan?.name,
                plan_price: preview.newPlan?.price,
                diff_amount: preview.adjustment.realAmount,
                current_plan_name: preview.currentPlan?.name,
              },
            });
            return;
          }
          const response = await axios.post(`${API}/client/plan/change-pagarme`, {
            target_plan_id: planKey,
            idempotency_key: preview.idempotencyKey,
          }, { headers });
          toast.success(response.data.message || 'Plano alterado com sucesso!');
          navigate('/client/dashboard');
          return;
        }

        const isNative = Capacitor.isNativePlatform();
        const originUrl = isNative ? 'https://jatoo.app.br' : window.location.origin;

        const response = await axios.post(`${API}/client/plan/change`, {
          new_plan_key: planKey,
          idempotency_key: `migrate_plan_${subscription?.id}_${Date.now()}`,
          origin_url: originUrl
        }, { headers });
        
        // Verificar se requer pagamento (upgrade)
        if (response.data.requires_payment && response.data.checkout_url) {
          toast.info('Redirecionando para pagamento...');
          if (isNative) {
            await Browser.open({ url: response.data.checkout_url });
          } else {
            window.location.href = response.data.checkout_url;
          }
          return;
        }
        
        // Downgrade ou mesmo nível - aplicado imediatamente
        toast.success(response.data.message || 'Plano alterado com sucesso!');
        navigate('/client/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar migração');
      setProcessing(false);
    }
  };

  const handleBack = () => {
    if (step === 'preview') {
      setStep(changeType === 'vehicle' ? 'vehicle_form' : 'plan_select');
      setPreview(null);
    } else if (step === 'vehicle_form' || step === 'plan_select') {
      setStep('select');
      setChangeType(null);
      setNewVehicle({ brand: '', model: '', plate: '', vehicle_type: '' });
      setModelInput('');
      setModelSuggestions([]);
      setSelectedPlan(null);
      setClassifiedType(null);
      setNeedsManualSelection(false);
    } else {
      navigate('/client/plan-details');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="migrate-plan-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleBack}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold">Migrar Plano</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Plano Atual */}
        <Card className="p-4 bg-primary/5 border-primary/20">
          <p className="text-xs text-muted-foreground mb-1">Plano Atual</p>
          <p className="font-semibold">{subscription?.plan?.name || 'Plano'}</p>
          <p className="text-sm text-muted-foreground">
            {currentVehicle?.brand} {currentVehicle?.model} • {formatCurrency(subscription?.plan?.price)}/mês
          </p>
        </Card>

        {/* ETAPA: Seleção inicial */}
        {step === 'select' && (
          <Card className="p-5">
            <h3 className="font-semibold mb-4">O que você deseja alterar?</h3>
            
            <div className="space-y-3">
              {/* Opção: Trocar Veículo */}
              <button
                className="w-full p-4 rounded-lg border-2 text-left transition-all border-slate-200 hover:border-primary hover:bg-primary/5"
                onClick={() => handleSelectOption('vehicle')}
                data-testid="option-vehicle"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Car className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">Trocar Veículo</p>
                    <p className="text-sm text-muted-foreground">
                      Manter o plano atual, trocar o veículo vinculado
                    </p>
                  </div>
                </div>
              </button>

              {/* Opção: Trocar Plano */}
              <button
                className="w-full p-4 rounded-lg border-2 text-left transition-all border-slate-200 hover:border-primary hover:bg-primary/5"
                onClick={() => handleSelectOption('plan')}
                data-testid="option-plan"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">Trocar Plano</p>
                    <p className="text-sm text-muted-foreground">
                      Manter o veículo, mudar para outro plano
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </Card>
        )}

        {/* ETAPA: Formulário de Veículo */}
        {step === 'vehicle_form' && (
          <Card className="p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Car className="w-5 h-5 text-primary" />
              Novo Veículo
            </h3>

            <div className="space-y-4">
              {/* Marca - Usando BrandCombobox resiliente */}
              <div>
                <Label>Marca *</Label>
                <BrandCombobox
                  brands={brands}
                  value={newVehicle.brand}
                  onChange={handleBrandChange}
                  placeholder="Selecione a marca..."
                  testId="vehicle-brand-select"
                />
              </div>

              {/* Modelo com Autocomplete (igual ao OnboardingVehicle) */}
              <div className="relative">
                <Label>Modelo *</Label>
                <div className="relative">
                  <Input
                    ref={modelInputRef}
                    placeholder={newVehicle.brand ? "Digite o modelo (ex: Gol, Civic, Tera)..." : "Primeiro selecione a marca"}
                    value={modelInput}
                    onChange={handleModelInputChange}
                    onFocus={() => modelInput && setShowSuggestions(true)}
                    disabled={!newVehicle.brand}
                    className="pr-10"
                    autoComplete="off"
                    data-testid="vehicle-model-input"
                  />
                  {searchingModels && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                  )}
                  {!searchingModels && newVehicle.brand && (
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  )}
                </div>
                
                {/* Dropdown de sugestões */}
                {showSuggestions && modelSuggestions.length > 0 && (
                  <div 
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-[200px] overflow-auto"
                  >
                    {modelSuggestions.map((m) => (
                      <button
                        key={m.modelo}
                        type="button"
                        className="w-full px-3 py-2 text-left hover:bg-slate-100 flex items-center justify-between"
                        onClick={() => handleSelectModel(m)}
                      >
                        <span className="font-medium">{m.modelo}</span>
                        <span className="text-xs text-muted-foreground">{m.vehicle_type_display}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Nenhum resultado */}
                {showSuggestions && modelInput.length >= 2 && modelSuggestions.length === 0 && !searchingModels && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg p-3 text-sm text-muted-foreground">
                    Modelo não encontrado na base. Selecione o porte manualmente abaixo.
                  </div>
                )}
              </div>

              {classifiedType && !needsManualSelection && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Classificado como: <strong>{classifiedType.vehicle_type_display}</strong>
                  </AlertDescription>
                </Alert>
              )}

              {needsManualSelection && (
                <>
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      Modelo não encontrado. Selecione o tipo manualmente.
                    </AlertDescription>
                  </Alert>
                  <div>
                    <Label>Tipo do Veículo *</Label>
                    <Select 
                      value={newVehicle.vehicle_type} 
                      onValueChange={(v) => setNewVehicle(prev => ({ ...prev, vehicle_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Carro Pequeno</SelectItem>
                        <SelectItem value="suv">SUV</SelectItem>
                        <SelectItem value="pickup">Caminhonete</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div>
                <Label>Placa *</Label>
                <Input
                  placeholder="ABC-1234"
                  value={newVehicle.plate}
                  onChange={(e) => setNewVehicle(prev => ({ ...prev, plate: e.target.value.toUpperCase() }))}
                />
              </div>

              <Button 
                className="w-full" 
                onClick={handleVehiclePreview}
                disabled={processing || !newVehicle.brand || !newVehicle.model || !newVehicle.plate || (needsManualSelection && !newVehicle.vehicle_type)}
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Continuar
              </Button>
            </div>
          </Card>
        )}

        {/* ETAPA: Seleção de Plano */}
        {step === 'plan_select' && (
          <Card className="p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Selecione o Novo Plano
            </h3>

            <div className="space-y-3">
              {Array.isArray(plans) && plans.length > 0 ? (
                plans
                  .filter(p => p.key !== subscription?.plan?.key)
                  .map((plan) => (
                    <button
                      key={plan.key}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        selectedPlan?.key === plan.key 
                          ? 'border-primary bg-primary/5' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setSelectedPlan(plan)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{plan.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {plan.limits?.complete || 0} lavagens + {plan.limits?.ducha || 0} duchas
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-primary">{formatCurrency(plan.price)}</p>
                          <p className="text-xs text-muted-foreground">/mês</p>
                        </div>
                      </div>
                    </button>
                  ))
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  Nenhum plano alternativo disponível para seu tipo de veículo.
                </p>
              )}

              {selectedPlan && (
                <Button className="w-full mt-4" onClick={handlePlanPreview}>
                  Continuar
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* ETAPA: Preview */}
        {step === 'preview' && preview && (
          <>
            <Card className="p-5">
              <h3 className="font-semibold mb-4">Resumo da Migração</h3>

              {/* Preview de troca de veículo */}
              {preview.type === 'vehicle' && (
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground mb-2">Veículo</p>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 p-3 bg-red-50 rounded-lg text-center border border-red-200">
                      <p className="text-xs text-red-600 mb-1">Será removido</p>
                      <p className="font-medium text-sm">
                        {preview.currentVehicle?.brand} {preview.currentVehicle?.model}
                      </p>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {VEHICLE_TYPE_DISPLAY[preview.currentVehicle?.vehicle_type]}
                      </Badge>
                    </div>
                    <ArrowRightLeft className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 p-3 bg-green-50 rounded-lg text-center border border-green-200">
                      <p className="text-xs text-green-600 mb-1">Novo</p>
                      <p className="font-medium text-sm">
                        {preview.newVehicle?.brand} {preview.newVehicle?.model}
                      </p>
                      <Badge variant="default" className="mt-1 text-xs">
                        {preview.newVehicle?.vehicle_type_display}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview de troca de plano */}
              {preview.type === 'plan' && (
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground mb-2">Plano</p>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground mb-1">Atual</p>
                      <p className="font-medium text-sm">{preview.currentPlan?.name}</p>
                      <p className="text-sm text-muted-foreground">{formatCurrency(preview.currentPlan?.price)}/mês</p>
                    </div>
                    <ArrowRightLeft className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 p-3 bg-primary/5 rounded-lg text-center border border-primary/20">
                      <p className="text-xs text-muted-foreground mb-1">Novo</p>
                      <p className="font-medium text-sm">{preview.newPlan?.name}</p>
                      <p className="text-sm text-primary font-semibold">{formatCurrency(preview.newPlan?.price)}/mês</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Se troca de veículo muda o plano */}
              {preview.type === 'vehicle' && preview.currentPlan?.key !== preview.newPlan?.key && (
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground mb-2">Plano (ajuste automático)</p>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground mb-1">Atual</p>
                      <p className="font-medium text-sm">{preview.currentPlan?.name}</p>
                      <p className="text-sm text-muted-foreground">{formatCurrency(preview.currentPlan?.price)}/mês</p>
                    </div>
                    <ArrowRightLeft className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 p-3 bg-primary/5 rounded-lg text-center border border-primary/20">
                      <p className="text-xs text-muted-foreground mb-1">Novo</p>
                      <p className="font-medium text-sm">{preview.newPlan?.name}</p>
                      <p className="text-sm text-primary font-semibold">{formatCurrency(preview.newPlan?.price)}/mês</p>
                    </div>
                  </div>
                </div>
              )}

              <Separator className="my-4" />

              {/* Breakdown de serviços (para upgrade de veículo) */}
              {preview.type === 'vehicle' && preview.adjustment?.type === 'upgrade' && preview.servicesRemaining && (
                <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium mb-3">Cálculo da diferença (serviços restantes):</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-muted-foreground">
                        <th className="text-left py-1">Serviço</th>
                        <th className="text-center py-1">Qtd</th>
                        <th className="text-center py-1">Atual</th>
                        <th className="text-center py-1">Novo</th>
                        <th className="text-right py-1">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.servicesRemaining?.complete > 0 && (
                        <tr>
                          <td className="py-1">Lavagem</td>
                          <td className="text-center">{preview.servicesRemaining.complete}</td>
                          <td className="text-center">{formatCurrency(preview.adjustment?.breakdown?.complete?.current_value)}</td>
                          <td className="text-center">{formatCurrency(preview.adjustment?.breakdown?.complete?.new_value)}</td>
                          <td className="text-right font-medium">{formatCurrency(preview.adjustment?.breakdown?.complete?.subtotal)}</td>
                        </tr>
                      )}
                      {preview.servicesRemaining?.ducha > 0 && (
                        <tr>
                          <td className="py-1">Ducha</td>
                          <td className="text-center">{preview.servicesRemaining.ducha}</td>
                          <td className="text-center">{formatCurrency(preview.adjustment?.breakdown?.ducha?.current_value)}</td>
                          <td className="text-center">{formatCurrency(preview.adjustment?.breakdown?.ducha?.new_value)}</td>
                          <td className="text-right font-medium">{formatCurrency(preview.adjustment?.breakdown?.ducha?.subtotal)}</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t">
                        <td colSpan={4} className="py-2 font-semibold">Total a pagar</td>
                        <td className="text-right py-2 font-bold text-primary">{formatCurrency(preview.adjustment?.amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Impacto Financeiro */}
              <Alert className={
                preview.adjustment?.type === 'upgrade' 
                  ? 'bg-amber-50 border-amber-200'
                  : preview.adjustment?.type === 'downgrade'
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-green-50 border-green-200'
              }>
                {preview.adjustment?.type === 'upgrade' ? (
                  <CreditCard className="h-4 w-4 text-amber-600" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                )}
                <AlertDescription className={
                  preview.adjustment?.type === 'upgrade' 
                    ? 'text-amber-800'
                    : preview.adjustment?.type === 'downgrade'
                    ? 'text-blue-800'
                    : 'text-green-800'
                }>
                  {preview.adjustment?.message}
                  
                  {/* Valor para troca de plano (vem do Stripe preview) */}
                  {preview.type === 'plan' && preview.adjustment?.type === 'upgrade' && preview.adjustment?.realAmount > 0 && (
                    <p className="font-bold mt-2 text-lg">
                      Valor a pagar agora: {formatCurrency(preview.adjustment.realAmount)}
                      {preview.adjustment?.isStripeReal && (
                        <span className="text-xs font-normal ml-2 text-green-600">✓ Valor confirmado pelo Stripe</span>
                      )}
                    </p>
                  )}
                  
                  {/* Valor para troca de veículo (vem do cálculo de serviços restantes) */}
                  {preview.type === 'vehicle' && preview.adjustment?.type === 'upgrade' && preview.adjustment?.amount > 0 && (
                    <p className="font-bold mt-1">
                      Valor a pagar agora: {formatCurrency(preview.adjustment?.amount)}
                    </p>
                  )}
                </AlertDescription>
              </Alert>

              {/* Aviso de remoção de veículo */}
              {preview.type === 'vehicle' && (
                <Alert className="bg-slate-50 border-slate-200 mt-3">
                  <Trash2 className="h-4 w-4 text-slate-600" />
                  <AlertDescription className="text-slate-700">
                    O veículo antigo ({preview.currentVehicle?.brand} {preview.currentVehicle?.model}) será <strong>removido automaticamente</strong>.
                  </AlertDescription>
                </Alert>
              )}
            </Card>

            {/* Botões de Ação */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={handleBack}
                disabled={processing}
              >
                Voltar
              </Button>
              <Button 
                className="flex-1"
                onClick={handleConfirm}
                disabled={processing}
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmar
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
